import { useCallback, useEffect, useRef, useState } from 'react';
import { BORNES_PAPIER, type FondDePapier } from '@nextilt/commun';
import type { ChangementDeLigne, ChangementDePapier, Papier } from '../partage/api/types';
import {
  AJOUTER_UNE_LIGNE,
  COCHER_LA_LIGNE,
  DECOCHER_LA_LIGNE,
  SUPPRIMER_LA_LISTE,
  TITRE_DE_LA_LISTE,
  TITRE_DU_POST_IT,
  UNE_LIGNE_DE_LA_LISTE,
} from '../partage/vocabulaire';
import { CadreDuPapier } from './CadreDuPapier';
import {
  apresEffacement,
  apresEntree,
  apresEntreeDuTitre,
  peutAjouterUneLigne,
  quitterUneLigne,
  type OuVaLeCurseur,
} from './reglesDeLaListe';
import { ceQuiAChange } from './reglesDesPapiers';
import type { CarteDesPapiers } from './useGlisserUnPapier';

type Ligne = Papier['lignes'][number];

/**
 * Une liste libre posée sur la carte (chantier post-it-et-listes, ticket 04 ; maquette
 * `.scratch/chantiers/carnet/maquettes/projet-ouvert.html`, `.liste`, `.case`, `.case.f`,
 * `.case.plus`). Une todo sans lien avec les bulles (décision d'Igor du 2026-10-19) : un titre,
 * des lignes à cocher, « Ajouter » en bas. Une ligne cochée est barrée et reste en place ; rien
 * ne passe par le moteur.
 *
 * Au clavier : Entrée dans la dernière ligne en crée une suivante (dans une autre, le curseur
 * descend) ; une ligne vidée puis quittée est retirée ; effacer dans une ligne vide remonte à
 * celle d'avant ; Échap quitte la ligne sans fermer rien d'autre.
 *
 * Sa place, sa taille, le glisser, la poignée, son papier et la croix sont ceux de tout papier
 * (`CadreDuPapier`).
 */
export function ListeLibre({
  papier,
  carte,
  aEcrire,
  onEcrire,
  onPlacer,
  onChangerDeFond,
  onSupprimer,
  onAjouterUneLigne,
  onChangerUneLigne,
  onRetirerUneLigne,
}: {
  papier: Papier;
  carte: CarteDesPapiers;
  /** Elle vient d'être posée : le curseur se met dans son titre. */
  aEcrire: boolean;
  onEcrire: (id: string, changement: ChangementDePapier) => void;
  onPlacer: (id: string, changement: ChangementDePapier) => Promise<void>;
  onChangerDeFond: (id: string, fond: FondDePapier) => void;
  onSupprimer: (id: string) => void;
  onAjouterUneLigne: (papierId: string) => Promise<string | null>;
  onChangerUneLigne: (papierId: string, ligneId: string, changement: ChangementDeLigne) => void;
  onRetirerUneLigne: (papierId: string, ligneId: string) => void;
}) {
  // ── Le titre : écrit à même la liste, parti à la sortie du champ, comme celui d'un post-it ──
  const [titre, setTitre] = useState(papier.titre ?? '');
  const ecritLeTitre = useRef(false);
  useEffect(() => {
    if (!ecritLeTitre.current) setTitre(papier.titre ?? '');
  }, [papier.titre]);
  const dernier = useRef({ papier, titre, onEcrire });
  dernier.current = { papier, titre, onEcrire };
  const enregistrerLeTitre = useCallback(() => {
    const d = dernier.current;
    const changement = ceQuiAChange(d.papier, { titre: d.titre, texte: d.papier.texte });
    if (changement) d.onEcrire(d.papier.id, changement);
  }, []);
  useEffect(() => () => enregistrerLeTitre(), [enregistrerLeTitre]);

  // ── Où va le curseur : posé quand la ligne visée est à l'écran ──
  const champDuTitre = useRef<HTMLInputElement>(null);
  const champs = useRef(new Map<string, HTMLTextAreaElement>());
  const [curseur, setCurseur] = useState<string | null>(null);
  useEffect(() => {
    if (!curseur) return;
    const champ = curseur === 'titre' ? champDuTitre.current : champs.current.get(curseur);
    if (!champ) return;
    setCurseur(null);
    champ.focus();
    const fin = champ.value.length;
    champ.setSelectionRange(fin, fin);
  }, [curseur, papier.lignes]);

  const aller = (ou: OuVaLeCurseur, depuis: HTMLElement) => {
    if (ou.sorte === 'aller') setCurseur(ou.id);
    else if (ou.sorte === 'titre') setCurseur('titre');
    else if (ou.sorte === 'quitter') depuis.blur();
    else void onAjouterUneLigne(papier.id).then((id) => id && setCurseur(id));
  };

  const ajouter = () => {
    void onAjouterUneLigne(papier.id).then((id) => id && setCurseur(id));
  };

  return (
    <CadreDuPapier
      papier={papier}
      carte={carte}
      supprimer={SUPPRIMER_LA_LISTE}
      onPlacer={onPlacer}
      onChangerDeFond={onChangerDeFond}
      onSupprimer={onSupprimer}
    >
      <input
        ref={champDuTitre}
        className="papier-titre liste-titre"
        value={titre}
        maxLength={BORNES_PAPIER.titreMax}
        placeholder={TITRE_DU_POST_IT}
        aria-label={TITRE_DE_LA_LISTE}
        autoFocus={aEcrire}
        onFocus={() => {
          ecritLeTitre.current = true;
        }}
        onChange={(e) => setTitre(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            aller(apresEntreeDuTitre(papier.lignes), e.currentTarget);
          } else if (e.key === 'Escape') {
            // Échap ne fait que quitter le titre : rien d'autre ne se ferme.
            e.stopPropagation();
            e.currentTarget.blur();
          }
        }}
        onBlur={() => {
          ecritLeTitre.current = false;
          enregistrerLeTitre();
        }}
      />
      <div className="liste-lignes">
        {papier.lignes.map((ligne) => (
          <LigneDeListe
            key={ligne.id}
            ligne={ligne}
            champ={(el) => {
              if (el) champs.current.set(ligne.id, el);
              else champs.current.delete(ligne.id);
            }}
            onEntree={(ecrit, depuis) => aller(apresEntree(papier.lignes, ligne.id, ecrit), depuis)}
            onEffacer={() => aller(apresEffacement(papier.lignes, ligne.id), document.body)}
            onQuitter={(ecrit) => {
              const geste = quitterUneLigne(ligne, ecrit);
              if (geste?.sorte === 'retirer') onRetirerUneLigne(papier.id, ligne.id);
              else if (geste?.sorte === 'ecrire') onChangerUneLigne(papier.id, ligne.id, { texte: geste.texte });
            }}
            onCocher={() => onChangerUneLigne(papier.id, ligne.id, { cochee: !ligne.cochee })}
          />
        ))}
        {peutAjouterUneLigne(papier.lignes) && (
          <button type="button" className="liste-ajouter" onClick={ajouter}>
            <span className="liste-ajouter-plus" aria-hidden="true">
              +
            </span>
            {AJOUTER_UNE_LIGNE}
          </button>
        )}
      </div>
    </CadreDuPapier>
  );
}

/**
 * Une ligne : une case et son texte. Le texte part à la sortie du champ ; vidée, la ligne part.
 * Une ligne longue passe à la ligne suivante du papier, sans rien cacher.
 */
function LigneDeListe({
  ligne,
  champ,
  onEntree,
  onEffacer,
  onQuitter,
  onCocher,
}: {
  ligne: Ligne;
  champ: (el: HTMLTextAreaElement | null) => void;
  onEntree: (ecrit: string, depuis: HTMLElement) => void;
  onEffacer: () => void;
  onQuitter: (ecrit: string) => void;
  onCocher: () => void;
}) {
  const [ecrit, setEcrit] = useState(ligne.texte);
  const enCours = useRef(false);
  useEffect(() => {
    if (!enCours.current) setEcrit(ligne.texte);
  }, [ligne.texte]);

  /*
   * La carte s'en va pendant qu'on écrit (une autre bulle) : ce qui est écrit part quand même. Une
   * ligne vide n'est jamais retirée d'ici : en mode strict, React défait et refait les effets d'une
   * ligne qui vient de naître, le curseur déjà dedans, et elle partait aussitôt (vu au ticket 04).
   * Écrire deux fois le même texte (ici, puis à la sortie du champ) ne change rien.
   */
  const dernier = useRef({ ecrit, onQuitter });
  dernier.current = { ecrit, onQuitter };
  useEffect(
    () => () => {
      if (enCours.current && dernier.current.ecrit.trim() !== '') dernier.current.onQuitter(dernier.current.ecrit);
    },
    []
  );

  return (
    <div className={`liste-ligne${ligne.cochee ? ' liste-ligne--cochee' : ''}`} data-ligne={ligne.id}>
      <button
        type="button"
        role="checkbox"
        aria-checked={ligne.cochee}
        className="liste-case"
        aria-label={ligne.cochee ? DECOCHER_LA_LIGNE : COCHER_LA_LIGNE}
        title={ligne.cochee ? DECOCHER_LA_LIGNE : COCHER_LA_LIGNE}
        onClick={onCocher}
      />
      <textarea
        ref={champ}
        className="liste-texte"
        value={ecrit}
        rows={1}
        maxLength={BORNES_PAPIER.ligneMax}
        aria-label={UNE_LIGNE_DE_LA_LISTE}
        onFocus={() => {
          enCours.current = true;
        }}
        onChange={(e) => setEcrit(e.target.value.replace(/\n/g, ' '))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onEntree(ecrit, e.currentTarget);
          } else if (e.key === 'Backspace' && ecrit === '') {
            e.preventDefault();
            onEffacer();
          } else if (e.key === 'Escape') {
            // Échap ne fait que quitter la ligne : rien d'autre ne se ferme.
            e.stopPropagation();
            e.currentTarget.blur();
          }
        }}
        onBlur={() => {
          if (!enCours.current) return;
          enCours.current = false;
          onQuitter(ecrit);
        }}
      />
    </div>
  );
}
